import React, { useState, useEffect, useCallback, useRef } from "react";
import { Typography, Flex, Input, Button, Table, DatePicker, Tooltip, Empty, message, Spin } from "antd";
import { SearchOutlined, DownloadOutlined, MailOutlined, CalculatorOutlined, ArrowRightOutlined, EditOutlined } from "@ant-design/icons";
import { getQuotations } from "../../../services/quotationService";
import { getClients } from "../../../services/clientService";
import useAuthSession from "../../../hooks/useAuthSession";
import QuotationModal from "../quotation/QuotationModal";

const { Title, Text } = Typography;

function QuotationsView({ role: propRole }) {
  const { companyCode: effectiveCompanyCode, employeeId } = useAuthSession();

  const [quotations, setQuotations] = useState([]);
  const [allClients, setAllClients] = useState([]);
  const [quotationCompanies, setQuotationCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [railLoading, setRailLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalAllQuotesCount, setTotalAllQuotesCount] = useState(0);

  const [selectedLead, setSelectedLead] = useState(null);
  const [quoteSearch, setQuoteSearch] = useState("");
  const [dateFilter, setDateFilter] = useState(null);

  const [isQuotationModalOpen, setIsQuotationModalOpen] = useState(false);
  const [selectedQuotationRecord, setSelectedQuotationRecord] = useState(null);
  const [selectedQuotationLead, setSelectedQuotationLead] = useState(null);

  const activeRequestIdRef = useRef(0);
  const activeAbortControllerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (activeAbortControllerRef.current) {
        activeAbortControllerRef.current.abort();
      }
    };
  }, []);

  const handleOpenQuotation = useCallback((record) => {
    setSelectedQuotationRecord(record);
    const matchedLead = allClients.find((c) => c.clientId === record.leadId || c.clientId === record.clientId || c.leadCompanyName === record.leadCompanyName);
    setSelectedQuotationLead(matchedLead || {
      id: record.leadId || record.clientId,
      leadCompanyName: record.leadCompanyName,
      contactName: record.contactName,
      contactNumber: record.contactNumber,
      directorEmailAddress: record.directorEmailAddress,
      gstNumber: record.gstNumber,
    });
    setIsQuotationModalOpen(true);
  }, [allClients]);

  const handleOpenMail = useCallback((record) => {
    handleOpenQuotation(record);
  }, [handleOpenQuotation]);

  // Load all clients for left rail
  const loadClientsList = useCallback(async () => {
    if (!effectiveCompanyCode) return;
    try {
      const res = await getClients(effectiveCompanyCode);
      const list = res?.clients || res?.items || res?.data || (Array.isArray(res) ? res : []);
      setAllClients(list);
    } catch (err) {
      console.error("Failed to load clients for quotations rail:", err);
    }
  }, [effectiveCompanyCode]);

  useEffect(() => {
    loadClientsList();
  }, [loadClientsList]);

  // Load Live Quotations from Middleware with Pagination & Race-condition Guard
  const loadQuotations = useCallback(async () => {
    if (!effectiveCompanyCode) return;

    if (activeAbortControllerRef.current) {
      activeAbortControllerRef.current.abort();
    }
    const controller = new AbortController();
    activeAbortControllerRef.current = controller;

    const currentRequestId = ++activeRequestIdRef.current;
    setLoading(true);

    try {
      const options = {
        page: currentPage,
        pageSize: pageSize,
        signal: controller.signal,
      };
      if (employeeId) options.employeeId = employeeId;
      if (selectedLead) {
        if (selectedLead.companyName && selectedLead.companyName !== "Prospective Enterprise") {
          options.companyName = selectedLead.companyName;
        }
        if (selectedLead.clientId) {
          options.clientId = selectedLead.clientId;
        }
        if (selectedLead.leadId) {
          options.leadId = selectedLead.leadId;
        }
      }
      if (quoteSearch.trim()) options.search = quoteSearch.trim();
      if (dateFilter) {
        options.dateFrom = dateFilter.format("YYYY-MM-DD");
        options.dateTo = dateFilter.format("YYYY-MM-DD");
      }

      const res = await getQuotations(effectiveCompanyCode, options);

      if (currentRequestId !== activeRequestIdRef.current) {
        return;
      }

      if (res.success) {
        setQuotations(res.quotations || []);
        setTotalCount(res.total || (res.quotations || []).length);
        if (!selectedLead && !quoteSearch.trim() && !dateFilter) {
          setTotalAllQuotesCount(res.total || (res.quotations || []).length);
        }
      }
    } catch (err) {
      if (controller.signal.aborted || currentRequestId !== activeRequestIdRef.current) {
        return;
      }
      console.error("Failed to load quotations from middleware:", err);
      message.error("Failed to fetch live quotations.");
    } finally {
      if (currentRequestId === activeRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [effectiveCompanyCode, employeeId, selectedLead, quoteSearch, dateFilter, currentPage, pageSize]);

  useEffect(() => {
    loadQuotations();
  }, [loadQuotations]);

  // Load all unique companies that actually have quotations
  const loadQuotationCompanies = useCallback(async () => {
    if (!effectiveCompanyCode) return;
    setRailLoading(true);
    try {
      const res = await getQuotations(effectiveCompanyCode, { page: 1, pageSize: 300 });
      if (res.success) {
        const quotes = res.quotations || [];
        setTotalAllQuotesCount(res.total || quotes.length);

        const compMap = new Map();
        quotes.forEach((q) => {
          const rawComp = (q.leadCompanyName || q.companyName || "").trim();
          const rawContact = (q.contactName || "").trim();
          const key = (rawComp || q.clientId || q.leadId || "Prospective Enterprise").toLowerCase();

          if (!compMap.has(key)) {
            const matchedClient = allClients.find(
              (c) =>
                (c.companyName && c.companyName.trim().toLowerCase() === rawComp.toLowerCase()) ||
                (c.clientId && q.clientId && c.clientId === q.clientId)
            );

            compMap.set(key, {
              key,
              clientId: q.clientId || matchedClient?.clientId || q.leadId || key,
              leadId: q.leadId || matchedClient?.leadId || "",
              companyName: rawComp || matchedClient?.companyName || "Prospective Enterprise",
              primaryContact: rawContact || matchedClient?.primaryContactName || matchedClient?.contactName || "Contact Person",
              phone: q.contactNumber || matchedClient?.primaryPhone || matchedClient?.phone || "",
              email: q.directorEmailAddress || matchedClient?.primaryEmail || matchedClient?.email || "",
              quoteCount: 1,
            });
          } else {
            compMap.get(key).quoteCount += 1;
          }
        });

        const list = Array.from(compMap.values()).sort((a, b) => b.quoteCount - a.quoteCount);
        setQuotationCompanies(list);
      }
    } catch (err) {
      console.error("Failed to load quotation companies:", err);
    } finally {
      setRailLoading(false);
    }
  }, [effectiveCompanyCode, allClients]);

  useEffect(() => {
    loadQuotationCompanies();
  }, [loadQuotationCompanies]);

  // Dynamic leads in left rail ONLY includes companies with quotations
  const dynamicLeads = quotationCompanies;

  // Filtered Leads in Left Rail
  const filteredLeads = dynamicLeads;

  const handleSelectLead = (lead) => {
    if (activeAbortControllerRef.current) {
      activeAbortControllerRef.current.abort();
    }
    activeRequestIdRef.current++;

    const isSameSelected = Boolean(
      lead &&
        selectedLead &&
        ((selectedLead.key && selectedLead.key === lead.key) ||
          (selectedLead.companyName && lead.companyName && selectedLead.companyName.toLowerCase() === lead.companyName.toLowerCase()))
    );

    if (!lead || isSameSelected) {
      setSelectedLead(null);
    } else {
      setSelectedLead(lead);
    }
    setQuotations([]);
    setTotalCount(0);
    setLoading(true);
    setCurrentPage(1);
  };

  const handleQuoteSearchChange = (val) => {
    if (activeAbortControllerRef.current) {
      activeAbortControllerRef.current.abort();
    }
    activeRequestIdRef.current++;
    setQuoteSearch(val);
    setQuotations([]);
    setTotalCount(0);
    setLoading(true);
    setCurrentPage(1);
  };

  const handleDateFilterChange = (d) => {
    if (activeAbortControllerRef.current) {
      activeAbortControllerRef.current.abort();
    }
    activeRequestIdRef.current++;
    setDateFilter(d);
    setQuotations([]);
    setTotalCount(0);
    setLoading(true);
    setCurrentPage(1);
  };

  // Table Columns
  const columns = [
    {
      title: "Quotation #",
      dataIndex: "quotationNumber",
      key: "quotationNumber",
      render: (val, r) => (
        <Flex vertical style={{ cursor: "pointer" }} onClick={() => handleOpenQuotation(r, false)}>
          <Text strong style={{ color: "#2459d3", fontSize: "0.9rem" }}>
            {val || "QT-NEW"}
          </Text>
          {r.versionNo && (
            <Text type="secondary" style={{ fontSize: "0.72rem" }}>
              v{r.versionNo} &bull;{" "}
              {(() => {
                const isHexId = (val) => typeof val === "string" && /^[0-9a-fA-F]{24}$/.test(val.trim());
                const author = [r.employeeName, r.createdByName, r.employee?.name, r.createdBy?.name].find(
                  (val) => val && typeof val === "string" && !isHexId(val)
                );
                return author ? `By: ${author}` : "Direct";
              })()}
            </Text>
          )}
        </Flex>
      ),
    },
    {
      title: "Company / Lead",
      dataIndex: "leadCompanyName",
      key: "leadCompanyName",
      render: (val, r) => (
        <Flex vertical>
          <Text strong style={{ fontSize: "0.88rem", color: "#0f172a" }}>
            {val || r.companyName || "Prospective Enterprise"}
          </Text>
          <Text type="secondary" style={{ fontSize: "0.75rem" }}>
            {r.contactName || "—"} {r.contactNumber ? `(${r.contactNumber})` : ""}
          </Text>
        </Flex>
      ),
    },
    {
      title: "Date",
      dataIndex: "quotationDate",
      key: "quotationDate",
      render: (val, r) => {
        const d = val || r.createdAt;
        return (
          <Text style={{ fontSize: "0.82rem" }}>
            {d
              ? new Date(d).toLocaleDateString("en-IN", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })
              : "—"}
          </Text>
        );
      },
    },
    {
      title: "Items / Scope",
      key: "items",
      render: (_, r) => {
        const items = r.items || [];
        const firstItem = items[0]?.name || "Standard Proposal";
        const extraCount = items.length > 1 ? ` +${items.length - 1} more` : "";
        return (
          <Text style={{ fontSize: "0.82rem", color: "#334155" }}>
            {firstItem}
            {extraCount}
          </Text>
        );
      },
    },
    {
      title: "Taxable Amt",
      dataIndex: "subtotal",
      key: "subtotal",
      align: "right",
      render: (amt) => <Text style={{ fontSize: "0.85rem", color: "#64748b" }}>₹{Number(amt || 0).toLocaleString("en-IN")}</Text>,
    },
    {
      title: "Total (incl GST)",
      dataIndex: "total",
      key: "total",
      align: "right",
      render: (amt) => (
        <Text strong style={{ color: "#0f172a", fontSize: "0.92rem" }}>
          ₹{Number(amt || 0).toLocaleString("en-IN")}
        </Text>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      align: "right",
      render: (_, r) => (
        <Flex justify="end" gap={6}>
          <Tooltip title="Edit Quotation">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined style={{ color: "#2459d3" }} />}
              onClick={() => handleOpenQuotation(r, true)}
            />
          </Tooltip>

          <Tooltip title="View & Download Quotation">
            <Button type="text" size="small" icon={<DownloadOutlined style={{ color: "#2459d3" }} />} onClick={() => handleOpenQuotation(r, false)} />
          </Tooltip>

          <Tooltip title="Convert to Client Invoice">
            <Button type="text" size="small" icon={<ArrowRightOutlined style={{ color: "#10b981" }} />} onClick={() => handleOpenQuotation(r, false)} />
          </Tooltip>

          <Tooltip title="Send to Client via Email">
            <Button
              type="text"
              size="small"
              icon={<MailOutlined style={{ color: "#2459d3" }} />}
              onClick={() => handleOpenMail(r)}
            />
          </Tooltip>
        </Flex>
      ),
    },
  ];

  return (
    <div className="qa-spin-wrapper">
      <Flex vertical gap={20} className="quotations-workspace-container">
        {/* ── WORKSPACE SPLIT (LEFT RAIL + RIGHT TABLE) ── */}
        <div className="qa-workspace-frame">
          {/* ── LEFT RAIL: PROSPECTIVE LEADS ── */}
          <div className="qa-rail-pane">
            <Flex justify="space-between" align="center" className="qa-rail-header">
              <Flex align="center" gap={8}>
                <CalculatorOutlined style={{ color: "#2459d3", fontSize: 16 }} />
                <Text strong style={{ fontSize: "0.95rem" }}>
                  Quotations ({totalAllQuotesCount || totalCount})
                </Text>
              </Flex>
            </Flex>

            {/* Pinned All Quotations Option */}
            <div className="qa-rail-pinned">
              <button
                type="button"
                className={`qa-rail-item ${!selectedLead ? "active" : ""}`}
                onClick={() => handleSelectLead(null)}
                style={{ width: "100%", textAlign: "left", display: "block", fontFamily: "inherit" }}
              >
                <Text
                  strong
                  style={{
                    fontSize: "0.88rem",
                    color: !selectedLead ? "#2459d3" : "#0f172a",
                    display: "block",
                  }}
                >
                  All Quotations & Leads
                </Text>
                <Flex justify="space-between" align="center" style={{ marginTop: 4 }}>
                  <Text type="secondary" style={{ display: "block", fontSize: "0.74rem" }}>
                    View full live registry
                  </Text>
                  {(totalAllQuotesCount || totalCount) > 0 && (
                    <span className={`qa-count-badge ${!selectedLead ? "active" : ""}`}>
                      {totalAllQuotesCount || totalCount}
                    </span>
                  )}
                </Flex>
              </button>
            </div>

            <div className="qa-rail-list">
              {railLoading && filteredLeads.length === 0 ? (
                <div style={{ padding: "40px 0", textAlign: "center" }}>
                  <Spin size="small" tip="Loading companies..." />
                </div>
              ) : filteredLeads.length === 0 ? (
                <Empty description="No leads found" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ margin: "20px 0" }} />
              ) : (
                filteredLeads.map((lead) => {
                  const isSelected = Boolean(
                    selectedLead &&
                      ((selectedLead.key && selectedLead.key === lead.key) ||
                        (selectedLead.companyName && lead.companyName && selectedLead.companyName.toLowerCase() === lead.companyName.toLowerCase()))
                  );
                  const displayName = lead.companyName || lead.primaryContact || lead.clientId || "Prospective Enterprise";
                  const qCount = Number(lead.quoteCount || 0);
                  const contactDetails = [lead.primaryContact, lead.phone].filter(Boolean).join(" • ");
                  return (
                    <button
                      type="button"
                      key={lead.key || lead.companyName || lead.clientId}
                      className={`qa-rail-item ${isSelected ? "active" : ""}`}
                      onClick={() => handleSelectLead(lead)}
                      style={{ width: "100%", textAlign: "left", display: "block", fontFamily: "inherit" }}
                    >
                      <Flex vertical style={{ width: "100%" }}>
                        <Text
                          strong
                          style={{
                            fontSize: "0.88rem",
                            color: isSelected ? "#2459d3" : "#0f172a",
                            lineHeight: 1.35,
                            wordBreak: "break-word",
                          }}
                        >
                          {displayName}
                        </Text>
                        <Flex justify="space-between" align="center" style={{ width: "100%", marginTop: 4 }}>
                          <Text type="secondary" style={{ fontSize: "0.74rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, paddingRight: 6 }}>
                            {contactDetails || "—"}
                          </Text>
                          {qCount > 0 && (
                            <span className={`qa-count-badge ${isSelected ? "active" : ""}`}>
                              {qCount}
                            </span>
                          )}
                        </Flex>
                      </Flex>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          {/* ── RIGHT CONTENT: LIVE QUOTATION TABLE ── */}
          <div className="qa-content-pane">
            <Flex vertical gap={16}>
              {/* Header & Filter Row */}
              <Flex justify="space-between" align="center" wrap="wrap" gap={16} className="qa-content-header">
                <Flex vertical>
                  <Title level={4} style={{ margin: 0 }}>
                    {selectedLead ? selectedLead.companyName || "Lead Quotations" : "Live Quotation Registry"}
                  </Title>
                  <Text type="secondary" style={{ fontSize: "0.8rem" }}>
                    {totalCount} quotes found
                  </Text>
                </Flex>

                <Flex align="center" gap={12} wrap="wrap">
                  <Input prefix={<SearchOutlined style={{ color: "#94a3b8" }} />} placeholder="Search quotations..." value={quoteSearch} onChange={(e) => handleQuoteSearchChange(e.target.value)} allowClear style={{ width: 180 }} />

                  <DatePicker placeholder="Filter Date" value={dateFilter} onChange={(d) => handleDateFilterChange(d)} style={{ width: 140 }} />
                </Flex>
              </Flex>

              {/* Ant Design Quotations Table */}
              {loading && quotations.length === 0 ? (
                <div style={{ padding: "80px 0", textAlign: "center" }}>
                  <Spin size="large" tip="Loading quotations..." />
                </div>
              ) : (
                <Table
                  columns={columns}
                  dataSource={quotations}
                  loading={{
                    spinning: loading,
                    tip: "Loading quotations...",
                    size: "large",
                  }}
                  rowKey="_id"
                  locale={{
                    emptyText: <Empty description="No quotations found for current filters" style={{ padding: "4rem 0" }} />,
                  }}
                  pagination={{
                    current: currentPage,
                    pageSize: pageSize,
                    total: totalCount,
                    showSizeChanger: true,
                    pageSizeOptions: ["10", "20", "50", "100"],
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} quotations`,
                    onChange: (page, size) => {
                      setCurrentPage(page);
                      setPageSize(size);
                    },
                  }}
                  className="qa-antd-table"
                  size="middle"
                  scroll={{ x: 900 }}
                />
              )}
            </Flex>
          </div>
        </div>
      </Flex>

      {/* Quotation Builder Modal with native design and Mail attachment support */}
      <QuotationModal
        visible={isQuotationModalOpen}
        open={isQuotationModalOpen}
        onClose={() => {
          setIsQuotationModalOpen(false);
          setSelectedQuotationRecord(null);
          setSelectedQuotationLead(null);
        }}
        record={selectedQuotationRecord}
        savedQuotationRecord={selectedQuotationRecord}
        lead={selectedQuotationLead}
        companyCode={effectiveCompanyCode}
        employeeId={employeeId}
        onSaveQuotation={loadQuotations}
        onQuotationSent={loadQuotations}
      />
    </div>
  );
}

export default QuotationsView;
